import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Play, Pause, ShoppingCart, ChevronLeft, Check, Download, Users, Mic2, Clock, Heart, Layers, Upload, Loader2, Volume2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { useCart, useAuth } from "../App";
import SEO from "../components/SEO";
import { toast } from "sonner";
import WaveSurfer from "wavesurfer.js";
import { WishlistButton } from "../components/WishlistButton";
import { ReviewsSection } from "../components/ReviewsSection";
import { SocialShareButtons } from "../components/SocialShareButtons";
import { BeatRecommendations } from "../components/Recommendations";
import LikeButton from "../components/LikeButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const licenseDetails = {
  basic: {
    name: "Basic Lease",
    features: ["MP3 File", "Non-exclusive", "Up to 2,500 sales", "Must credit producer"],
    color: "neon-cyan"
  },
  premium: {
    name: "Premium Lease", 
    features: ["WAV + MP3 Files", "Trackout Stems", "Non-exclusive", "Up to 10,000 sales"],
    color: "neon-magenta"
  },
  unlimited: {
    name: "Unlimited Lease",
    features: ["WAV + MP3 Files", "Trackout Stems", "Non-exclusive", "Unlimited sales", "Radio play allowed"],
    color: "neon-green"
  },
  exclusive: {
    name: "Exclusive Rights",
    features: ["All files + Project", "Full ownership", "Beat removed from store", "Commercial use", "No credit required"],
    color: "neon-yellow"
  }
};

const BeatDetailPage = () => {
  const { beatId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  
  const [beat, setBeat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLicense, setSelectedLicense] = useState("basic");
  const [isPlaying, setIsPlaying] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [stems, setStems] = useState([]);
  
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    const fetchBeat = async () => {
      try {
        const [beatRes, stemsRes] = await Promise.all([
          axios.get(`${API}/beats/${beatId}`),
          axios.get(`${API}/beats/${beatId}/stems`).catch(() => ({ data: { stems: [] } }))
        ]);
        setBeat(beatRes.data);
        setStems(stemsRes.data.stems || []);
        // Track play + recommendation view
        axios.post(`${API}/beats/${beatId}/play`).catch(() => {});
        axios.post(`${API}/recommendations/track-view`, { beat_id: beatId }, {
          headers: localStorage.getItem("token") ? { Authorization: `Bearer ${localStorage.getItem("token")}` } : {},
          withCredentials: true
        }).catch(() => {});
      } catch (error) {
        toast.error("Beat not found");
        navigate("/beats");
      } finally {
        setLoading(false);
      }
    };
    fetchBeat();
  }, [beatId, navigate]);

  // Check if this beat uses SoundCloud for preview
  const isSoundCloudPreview = beat?.soundcloud_url || (beat?.preview_url && beat?.preview_url.includes("soundcloud.com"));
  const soundcloudEmbedUrl = isSoundCloudPreview
    ? `https://w.soundcloud.com/player/?url=${encodeURIComponent(beat?.soundcloud_url || beat?.preview_url)}&color=%2300f3ff&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=true`
    : null;

  useEffect(() => {
    isMountedRef.current = true;
    
    const initWaveSurfer = async () => {
      if (!beat || !waveformRef.current || isSoundCloudPreview) return;
      
      // Clean up existing
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.destroy();
        } catch (e) {}
        wavesurferRef.current = null;
      }

      try {
        const ws = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: '#3f3f46',
          progressColor: '#00f3ff',
          cursorColor: '#ff00ff',
          barWidth: 3,
          barGap: 2,
        barRadius: 0,
          height: 80,
          normalize: true,
        });

        wavesurferRef.current = ws;

        ws.on('ready', () => {
          if (isMountedRef.current) {
            setIsReady(true);
          }
        });

        ws.on('play', () => {
          if (isMountedRef.current) setIsPlaying(true);
        });
        
        ws.on('pause', () => {
          if (isMountedRef.current) setIsPlaying(false);
        });
        
        ws.on('finish', () => {
          if (isMountedRef.current) setIsPlaying(false);
        });

        ws.on('error', (err) => {
          console.warn('WaveSurfer error:', err);
        });

        await ws.load(beat.preview_url || beat.audio_url);
      } catch (error) {
        console.warn('WaveSurfer init error:', error);
      }
    };

    initWaveSurfer();

    return () => {
      isMountedRef.current = false;
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.pause();
          wavesurferRef.current.destroy();
        } catch (e) {}
        wavesurferRef.current = null;
      }
    };
  }, [beat?.beat_id]);

  const togglePlay = useCallback(() => {
    if (wavesurferRef.current && isReady) {
      wavesurferRef.current.playPause();
    }
  }, [isReady]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to cart");
      return;
    }
    
    if (selectedLicense === "exclusive" && beat.is_sold_exclusive) {
      toast.error("This beat has already been sold exclusively");
      return;
    }
    
    setAddingToCart(true);
    const success = await addToCart({
      item_type: "beat",
      item_id: beat.beat_id,
      license_type: selectedLicense,
      quantity: 1
    });
    setAddingToCart(false);
  };

  const getPrice = () => {
    if (!beat) return 0;
    return beat[`${selectedLicense}_price`] || beat.basic_price;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-neon-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!beat) return null;

  return (
    <div className="min-h-screen py-10 px-6" data-testid="beat-detail-page">
      {beat && <SEO title={beat.title} description={`${beat.genre} beat at ${beat.bpm} BPM in ${beat.key}. Starting at $${beat.basic_price}`} image={beat.image_url} type="music.song" />}
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/beats")}
          className="mb-6 text-zinc-400 hover:text-white"
          data-testid="back-button"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Beats
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left Column - Player */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass p-6 md:p-8"
            >
              {/* Beat Image */}
              <div className="relative aspect-square mb-6 overflow-hidden">
                <img
                  src={beat.image_url || "https://images.unsplash.com/photo-1741746239350-9021ddfa3d59?w=600"}
                  alt={beat.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                
                {/* Play Button Overlay - only for non-SoundCloud beats */}
                {!isSoundCloudPreview && (
                  <button
                    onClick={togglePlay}
                    className="absolute inset-0 flex items-center justify-center group"
                    data-testid="beat-play-button"
                  >
                    <div className="w-24 h-24 bg-neon-cyan/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-neon-cyan/30 transition-colors">
                      {isPlaying ? (
                        <Pause className="w-12 h-12 text-neon-cyan" fill="currentColor" />
                      ) : (
                        <Play className="w-12 h-12 text-neon-cyan" fill="currentColor" />
                      )}
                    </div>
                  </button>
                )}
              </div>

              {/* SoundCloud Player or Waveform */}
              {isSoundCloudPreview ? (
                <div className="mb-6" data-testid="soundcloud-embed">
                  <iframe
                    title="SoundCloud Player"
                    width="100%"
                    height="166"
                    scrolling="no"
                    frameBorder="no"
                    allow="autoplay"
                    src={soundcloudEmbedUrl}
                    style={{ borderRadius: 0, background: 'transparent' }}
                  />
                  {beat.needs_audio_upload && (
                    <p className="text-xs text-amber-400/70 mt-2 flex items-center gap-1">
                      <Upload className="w-3 h-3" /> Preview via SoundCloud. High-quality audio available after upload.
                    </p>
                  )}
                </div>
              ) : (
                <div ref={waveformRef} className="mb-6" />
              )}

              {/* Beat Info */}
              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="border-white/20 px-3 py-1">
                  <Clock className="w-3 h-3 mr-1" /> {beat.bpm} BPM
                </Badge>
                <Badge variant="outline" className="border-white/20 px-3 py-1">
                  <Mic2 className="w-3 h-3 mr-1" /> {beat.key}
                </Badge>
                <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 px-3 py-1">
                  {beat.genre}
                </Badge>
                <Badge variant="outline" className="border-white/20 px-3 py-1">
                  <Users className="w-3 h-3 mr-1" /> {beat.plays?.toLocaleString()} plays
                </Badge>
              </div>

              {/* Tags */}
              {beat.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {beat.tags.map((tag) => (
                    <span key={tag} className="text-xs text-zinc-500 bg-white/5 px-2 py-1">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Column - Licensing */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-2">
                {beat.title}
              </h1>
              
              <div className="flex items-center gap-3 mb-4">
                <WishlistButton itemType="beat" itemId={beat.beat_id} />
                <LikeButton itemType="beat" itemId={beat.beat_id} />
              </div>

              <SocialShareButtons title={beat.title} url={window.location.href} />

              {/* Vocal Preview */}
              <VocalPreview beatId={beat.beat_id} user={user} />
              
              {beat.is_sold_exclusive && (
                <Badge className="bg-red-500/20 text-red-500 border-red-500/50 mb-4">
                  Exclusive Sold
                </Badge>
              )}

              <div className="glass p-6 md:p-8 mt-6">
                <h2 className="font-heading text-xl uppercase tracking-wide mb-6">Select License</h2>
                
                <RadioGroup value={selectedLicense} onValueChange={setSelectedLicense} className="space-y-4">
                  {Object.entries(licenseDetails).map(([key, license]) => {
                    const price = beat[`${key}_price`];
                    const isExclusiveSold = key === "exclusive" && beat.is_sold_exclusive;
                    
                    return (
                      <div key={key} className={`relative ${isExclusiveSold ? 'opacity-50' : ''}`}>
                        <RadioGroupItem
                          value={key}
                          id={key}
                          disabled={isExclusiveSold}
                          className="peer sr-only"
                          data-testid={`license-${key}`}
                        />
                        <Label
                          htmlFor={key}
                          className={`flex items-center justify-between p-4 border cursor-pointer transition-all
                            ${selectedLicense === key 
                              ? `border-${license.color}/50 bg-${license.color}/10` 
                              : 'border-white/10 hover:border-white/20'
                            }
                            ${isExclusiveSold ? 'cursor-not-allowed' : ''}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                              ${selectedLicense === key ? `border-${license.color} bg-${license.color}` : 'border-zinc-600'}
                            `}>
                              {selectedLicense === key && <Check className="w-3 h-3 text-black" />}
                            </div>
                            <div>
                              <p className="font-medium">{license.name}</p>
                              {isExclusiveSold && (
                                <span className="text-xs text-red-500">Sold</span>
                              )}
                            </div>
                          </div>
                          <span className={`font-mono text-lg font-bold text-${license.color}`}>
                            ${price?.toFixed(2)}
                          </span>
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>

                {/* License Features */}
                <div className="mt-6 p-4 bg-white/5">
                  <h3 className="font-medium mb-3 text-sm uppercase tracking-wide text-zinc-400">
                    What's Included:
                  </h3>
                  <ul className="space-y-2">
                    {licenseDetails[selectedLicense].features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-neon-green" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Add to Cart */}
                <div className="mt-8 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Total:</span>
                    <span className="font-heading text-3xl text-neon-cyan">
                      ${getPrice().toFixed(2)}
                    </span>
                  </div>
                  <Button
                    onClick={handleAddToCart}
                    disabled={addingToCart || (selectedLicense === "exclusive" && beat.is_sold_exclusive)}
                    className="w-full bg-neon-cyan text-black font-bold uppercase tracking-widest py-6 btn-clip neon-cyan hover:scale-[1.02] transition-transform"
                    data-testid="add-to-cart-button"
                  >
                    {addingToCart ? (
                      "Adding..."
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Stems Section */}
        {stems.length > 0 && (
          <div className="mt-10 max-w-6xl mx-auto" data-testid="stems-section">
            <div className="glass p-6 md:p-8">
              <h2 className="font-heading text-xl uppercase tracking-wide mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-neon-green" />
                Available Stems / Trackouts
              </h2>
              <p className="text-sm text-zinc-400 mb-4">
                Individual instrument tracks available with Premium, Unlimited, and Exclusive licenses.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {stems.map((stem, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-sm"
                    data-testid={`stem-${idx}`}
                  >
                    <div className="w-10 h-10 bg-neon-green/20 flex items-center justify-center flex-shrink-0">
                      <Layers className="w-5 h-5 text-neon-green" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{stem.name}</p>
                      <p className="text-xs text-zinc-500">WAV / High Quality</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-zinc-600 mt-4">
                Stems are included with Premium ($79.99+), Unlimited, and Exclusive licenses.
              </p>
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="mt-10 max-w-6xl mx-auto">
          <ReviewsSection itemType="beat" itemId={beat.beat_id} />
        </div>

        {/* Recommendations */}
        <div className="max-w-6xl mx-auto">
          <BeatRecommendations beatId={beat.beat_id} />
        </div>
      </div>
    </div>
  );
};

export default BeatDetailPage;

function VocalPreview({ beatId, user }) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const audioRef = useRef(null);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { toast.error("Sign in to preview"); return; }

    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) { toast.error("Max 15MB file size"); return; }

    setUploading(true);
    setPreviewUrl(null);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("vocal", file);
      const res = await axios.post(`${API}/beats/${beatId}/vocal-preview`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 60000
      });
      const url = res.data.audio_url.startsWith("http") ? res.data.audio_url : `${BACKEND_URL}${res.data.audio_url}`;
      setPreviewUrl(url);
      toast.success("Preview ready! Hit play to listen.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process audio");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const togglePreviewPlay = () => {
    if (!audioRef.current) return;
    if (previewPlaying) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setPreviewPlaying(!previewPlaying);
  };

  return (
    <div className="mt-4 p-4 glass border border-neon-magenta/20 rounded-lg" data-testid="vocal-preview-section">
      <div className="flex items-center gap-2 mb-3">
        <Mic2 className="w-4 h-4 text-neon-magenta" />
        <span className="text-sm font-medium text-white">Preview with Your Voice</span>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Upload a vocal clip to hear it mixed over this beat.</p>

      <div className="flex items-center gap-3">
        <input ref={fileRef} type="file" accept="audio/*" onChange={handleUpload} className="hidden" id={`vocal-upload-${beatId}`} />
        <label htmlFor={`vocal-upload-${beatId}`}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold cursor-pointer transition ${
            uploading ? "bg-zinc-800 text-zinc-500" : "bg-neon-magenta/20 text-neon-magenta hover:bg-neon-magenta/30 border border-neon-magenta/30"
          }`}
          data-testid="upload-vocal-btn"
        >
          {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing...</> : <><Upload className="w-3.5 h-3.5" /> Upload Vocal</>}
        </label>

        {previewUrl && (
          <>
            <Button size="sm" onClick={togglePreviewPlay}
              className="bg-neon-magenta text-black font-bold" data-testid="play-vocal-preview-btn"
            >
              {previewPlaying ? <Pause className="w-3.5 h-3.5 mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
              {previewPlaying ? "Pause" : "Play Mix"}
            </Button>
            <audio ref={audioRef} src={previewUrl} onEnded={() => setPreviewPlaying(false)} />
          </>
        )}
      </div>
      <p className="text-[10px] text-zinc-600 mt-2">Supported: MP3, WAV, OGG, M4A (max 15MB, 60s preview)</p>
    </div>
  );
}
